import React, { useEffect, useRef, useState } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { ReactComponent as Logo } from './logo.svg';
import LoginPage from './pages/LoginPage';
import AdminLoginPage from './pages/AdminLoginPage';
import EmployeeLoginPage from './pages/EmployeeLoginPage';
import EmployeeDashboard from './pages/EmployeeDashboard';
import RegistrationPage from './pages/RegistrationPage';
import MerchantDashboard from './pages/MerchantDashboard';
import AdminLayout from './layouts/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminMerchant from './pages/admin/AdminMerchant';
import AdminMerchantView from './pages/admin/AdminMerchantView';
import AdminSubscriptionConfig from './pages/admin/AdminSubscriptionConfig';
import AdminAccounting from './pages/admin/AdminAccounting';
import AdminSettings from './pages/admin/AdminSettings';

function useAuth() {
  return !!localStorage.getItem('auth_token');
}

function useAdminAuth() {
  return !!localStorage.getItem('admin_token');
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuth = useAuth();
  if (!isAuth) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AdminProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAdmin = useAdminAuth();
  if (!isAdmin) return <Navigate to="/admin/login" replace />;
  return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const isAuth = useAuth();
  const location = useLocation();
  if (isAuth) return <Navigate to="/dashboard" replace state={{ from: location }} />;
  return <>{children}</>;
}

function AdminGuestRoute({ children }: { children: React.ReactNode }) {
  const isAdmin = useAdminAuth();
  if (isAdmin) return <Navigate to="/admin/dashboard" replace />;
  return <>{children}</>;
}

// ----- Animated counter for stats -----
function AnimatedNumber({ value, suffix = '', duration = 1.5 }: { value: number; suffix?: string; duration?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const isDecimal = value % 1 !== 0;
    const end = value;
    const start = 0;
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = (now - startTime) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = start + (end - start) * easeOut;
      setCount(isDecimal ? Math.round(current * 10) / 10 : Math.round(current));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

// ----- Header -----
const navLinks = [
  { label: 'Features', href: '#features' },
  { label: 'Industries', href: '#industries' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'Pricing', href: '#pricing' },
];

function Header() {
  const { scrollY } = useScroll();
  const bgOpacity = useTransform(scrollY, [0, 80], [0, 0.95]);
  const shadow = useTransform(scrollY, [0, 80], [0, 1]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <motion.header
      style={{
        backgroundColor: useTransform(bgOpacity, (v) => `rgba(255,255,255,${v})`),
        boxShadow: useTransform(shadow, (v) => `0 1px 0 rgba(0,0,0,${v * 0.06})`),
      }}
      className="fixed top-0 left-0 right-0 z-50 transition-colors duration-300"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <motion.a
            href="/"
            className="flex items-center gap-2"
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <span className="w-9 h-9 rounded-lg bg-brand-600 flex items-center justify-center text-white shadow-lg shadow-brand-600/30">
              <Logo className="w-5 h-5 shrink-0" aria-hidden />
            </span>
            <span className="font-bold text-lg sm:text-xl text-gray-900">SpynERP</span>
          </motion.a>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link, i) => (
              <motion.a
                key={link.href}
                href={link.href}
                className="text-gray-600 hover:text-gray-900 font-medium text-sm transition-colors"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.05 }}
              >
                {link.label}
              </motion.a>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setMobileMenuOpen((o) => !o)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            <Link to={localStorage.getItem('auth_token') ? '/dashboard' : '/login'} className="shrink-0">
              <motion.span
                className="rounded-lg bg-brand-600 px-3 py-2 sm:px-4 sm:py-2.5 text-white text-sm font-semibold hover:bg-brand-700 transition-colors shadow-lg shadow-brand-600/25 hover:shadow-brand-600/35 inline-block"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.15 }}
              >
                <span className="hidden sm:inline">Go to Dashboard →</span>
                <span className="sm:hidden">Dashboard</span>
              </motion.span>
            </Link>
          </div>
        </div>
      </div>
      {/* Mobile menu dropdown */}
      {mobileMenuOpen && (
        <motion.nav
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden border-t border-gray-100 bg-white/98 backdrop-blur"
        >
          <div className="px-4 py-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="py-2.5 px-3 rounded-lg text-gray-700 font-medium hover:bg-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
          </div>
        </motion.nav>
      )}
    </motion.header>
  );
}

// ----- Hero -----
function Hero() {
  return (
    <section className="relative min-h-screen flex flex-col justify-center pt-24 pb-20 px-4 gradient-mesh overflow-hidden">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 rounded-full bg-sky-50 border border-sky-200/80 text-sky-700 text-sm font-medium px-4 py-2 mb-8"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
          </svg>
          Next-Generation ERP Platform
        </motion.div>

        <motion.h1
          className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight mb-6 leading-tight"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          Run Your Business{' '}
          <span className="text-brand-600 bg-clip-text">Smarter</span>, Not Harder
        </motion.h1>

        <motion.p
          className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
        >
          SpynERP unifies your accounting, sales, inventory, HR, and operations into one powerful platform. Built for businesses that demand excellence.
        </motion.p>

        <motion.div
          className="flex flex-wrap items-center justify-center gap-4 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.35 }}
        >
          <Link to="/register">
          <motion.span
            className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-6 py-3.5 text-white font-semibold text-base shadow-xl shadow-brand-600/30 hover:bg-brand-700 hover:shadow-brand-600/40 transition-all hover:-translate-y-0.5 block"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Start Free Trial →
          </motion.span>
        </Link>
          <motion.a
            href="#features"
            className="inline-flex items-center gap-2 rounded-xl border-2 border-gray-300 bg-white px-6 py-3.5 text-gray-700 font-semibold text-base hover:border-gray-400 hover:bg-gray-50 transition-all"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600">▶</span>
            Watch Demo
          </motion.a>
        </motion.div>

        <motion.div
          className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {['No credit card required', '14-day free trial', 'Cancel anytime'].map((text, i) => (
            <span key={i} className="inline-flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-xs">✓</span>
              {text}
            </span>
          ))}
        </motion.div>
      </div>

      {/* Dashboard preview */}
      <motion.div
        id="dashboard"
        className="mt-16 max-w-5xl mx-auto px-4"
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
      >
        <div className="rounded-2xl overflow-hidden dashboard-glow border border-gray-200/80 bg-gray-900/95 backdrop-blur">
          <div className="flex items-center gap-2 px-4 py-3 bg-gray-800/80 border-b border-gray-700/50">
            <span className="w-3 h-3 rounded-full bg-red-500/90" />
            <span className="w-3 h-3 rounded-full bg-amber-500/90" />
            <span className="w-3 h-3 rounded-full bg-emerald-500/90" />
          </div>
          <div className="p-6 sm:p-8 min-h-[320px]">
            <div className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-4">USERS: LAST 7 DAYS USING MEDIAN</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: 'LOAD TIME VS BOUNCE RATE', median: '2.056s', color: 'from-cyan-400 to-blue-500' },
                { title: 'START RENDER VS BOUNCE RATE', median: '1.031s', color: 'from-violet-400 to-purple-500' },
              ].map((chart, i) => (
                <motion.div
                  key={i}
                  className="rounded-xl bg-gray-800/60 p-4 border border-gray-700/50"
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 + i * 0.1 }}
                >
                  <div className="text-gray-300 text-xs mb-3">{chart.title}</div>
                  <div className="h-24 flex items-end gap-1">
                    {[40, 65, 45, 80, 55, 70, 90].map((h, j) => (
                      <motion.div
                        key={j}
                        className={`flex-1 rounded-t bg-gradient-to-t ${chart.color} opacity-90`}
                        initial={{ height: 0 }}
                        animate={{ height: `${h}%` }}
                        transition={{ duration: 0.6, delay: 1 + j * 0.05 }}
                      />
                    ))}
                  </div>
                  <div className="text-gray-500 text-xs mt-2">Median: {chart.median}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

// ----- Stats strip -----
const stats = [
  { value: 99.9, suffix: '%', label: 'Uptime SLA' },
  { value: 50, suffix: '+', label: 'Integrations' },
  { value: 24, suffix: '/7', label: 'Support' },
  { value: 256, suffix: '-bit', label: 'Encryption' },
];

function Stats() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section ref={ref} className="py-16 sm:py-20 bg-navy-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              className="text-center"
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-1">
                <AnimatedNumber value={stat.value} suffix={stat.suffix} />
              </div>
              <div className="text-gray-400 font-medium text-sm sm:text-base">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ----- Features -----
const features = [
  {
    title: 'Accounting & Finance',
    description: 'Complete GL, AR, AP management with automated journal entries and bank reconciliation.',
    icon: '∑',
    color: 'bg-blue-500',
    href: '#features',
  },
  {
    title: 'Sales & CRM',
    description: 'Manage customers, create professional invoices, and track your sales pipeline.',
    icon: '↗',
    color: 'bg-emerald-500',
    href: '#features',
  },
  {
    title: 'Inventory Management',
    description: 'Real-time stock tracking, reorder alerts, and comprehensive product management.',
    icon: '▦',
    color: 'bg-amber-500',
    href: '#features',
  },
  {
    title: 'HR & Payroll',
    description: 'Employee management, payroll processing, and compliance tracking made simple.',
    icon: '◉',
    color: 'bg-brand-500',
    href: '#features',
  },
  {
    title: 'Project Management',
    description: 'Track projects, budgets, and billing with detailed job costing features.',
    icon: '☑',
    color: 'bg-rose-500',
    href: '#features',
  },
  {
    title: 'Reports & Analytics',
    description: 'Powerful financial reports and business intelligence dashboards.',
    icon: '▤',
    color: 'bg-indigo-500',
    href: '#features',
  },
];

function Features() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <section id="features" ref={ref} className="py-20 sm:py-28 bg-gray-50/80">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center max-w-2xl mx-auto mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-block rounded-lg bg-sky-50 text-sky-700 text-sm font-semibold px-3 py-1.5 mb-4 border border-sky-200/80">
            Powerful Features
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
            Everything You Need to Succeed
          </h2>
          <p className="text-gray-600 text-lg">
            A complete suite of tools designed to streamline every aspect of your business operations.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, i) => (
            <motion.a
              key={feature.title}
              href={feature.href}
              className="group rounded-2xl bg-white p-6 sm:p-8 shadow-card border border-gray-100 hover:shadow-card-hover hover:border-brand-200/50 transition-all duration-300"
              initial={{ opacity: 0, y: 32 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.05 * i }}
              whileHover={{ y: -4 }}
            >
              <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center text-white text-xl font-bold mb-5 shadow-lg`}>
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-brand-600 transition-colors">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                {feature.description}
              </p>
              <span className="inline-flex items-center gap-1 text-brand-600 font-semibold text-sm group-hover:gap-2 transition-all">
                Learn more →
              </span>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}

// ----- Industries -----
const industries = [
  { title: 'General Business', desc: 'Retail, services, and more', icon: '◈' },
  { title: 'Construction', desc: 'Job costing & BOQ management', icon: '⬡' },
  { title: 'Accounting Firms', desc: 'Multi-client bookkeeping', icon: '▣' },
];

function Industries() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });

  const barHeights = [60, 85, 70, 90, 78];

  return (
    <section id="industries" ref={ref} className="py-20 sm:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <motion.span
              className="inline-block rounded-lg bg-brand-50 text-brand-700 text-sm font-semibold px-3 py-1.5 mb-4 border border-brand-200/80"
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4 }}
            >
              Industry Solutions
            </motion.span>
            <motion.h2
              className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4"
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.05 }}
            >
              Built for Your Industry
            </motion.h2>
            <motion.p
              className="text-gray-600 text-lg mb-10"
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              SpynERP adapts to your specific industry needs with specialized modules and workflows. From general business to construction and accounting firms.
            </motion.p>

            <div className="space-y-3">
              {industries.map((ind, i) => (
                <motion.a
                  key={ind.title}
                  href="#industries"
                  className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-brand-50/80 border border-transparent hover:border-brand-200/60 transition-all group"
                  initial={{ opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.15 + i * 0.08 }}
                  whileHover={{ x: 4 }}
                >
                  <span className="w-10 h-10 rounded-lg bg-brand-100 text-brand-600 flex items-center justify-center font-bold text-lg group-hover:bg-brand-200 transition-colors">
                    {ind.icon}
                  </span>
                  <div className="flex-1">
                    <div className="font-bold text-gray-900 group-hover:text-brand-700">{ind.title}</div>
                    <div className="text-sm text-gray-500">{ind.desc}</div>
                  </div>
                  <span className="text-gray-400 group-hover:text-brand-600 transition-colors">→</span>
                </motion.a>
              ))}
            </div>
          </div>

          <motion.div
            className="rounded-2xl bg-gradient-to-br from-white to-brand-50/30 p-8 shadow-card border border-gray-100"
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="text-gray-600 font-medium">Monthly Revenue</div>
                <motion.div
                  className="text-3xl font-extrabold text-gray-900"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.5 }}
                >
                  ₱124,500
                </motion.div>
              </div>
              <span className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600">
                <span className="text-lg">↗</span>
              </span>
            </div>
            <div className="flex items-end gap-2 h-24 mb-8">
              {barHeights.map((h, i) => (
                <motion.div
                  key={i}
                  className="flex-1 rounded-t-lg bg-brand-500"
                  initial={{ height: 0 }}
                  animate={inView ? { height: `${h}%` } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + i * 0.08 }}
                />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
              {[
                { label: 'Invoices', value: '156' },
                { label: 'Customers', value: '89' },
                { label: 'Growth', value: '+23%', highlight: true },
              ].map((m, i) => (
                <div key={m.label}>
                  <div className="text-xs text-gray-500">{m.label}</div>
                  <motion.span
                    className={`font-bold ${m.highlight ? 'text-emerald-600' : 'text-gray-900'}`}
                    initial={{ opacity: 0 }}
                    animate={inView ? { opacity: 1 } : {}}
                    transition={{ delay: 0.6 + i * 0.05 }}
                  >
                    {m.value}
                  </motion.span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ----- Testimonials -----
const testimonials = [
  {
    quote: 'SpynERP transformed how we manage our finances. The automation saved us 20 hours per week.',
    name: 'Sarah Chen',
    role: 'CFO, TechStart Inc.',
    initial: 'S',
  },
  {
    quote: 'Finally an ERP that understands construction. Job costing has never been this accurate.',
    name: 'Michael Roberts',
    role: 'Operations Director, BuildRight',
    initial: 'M',
  },
  {
    quote: 'Managing 50+ client books is seamless. Our firm\'s productivity doubled.',
    name: 'Amanda Peters',
    role: 'Partner, Peters & Associates',
    initial: 'A',
  },
];

function Testimonials() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <section id="testimonials" ref={ref} className="py-20 sm:py-28 bg-navy-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-block rounded-lg bg-white/10 text-white/90 text-sm font-semibold px-3 py-1.5 mb-4 border border-white/20">
            Customer Stories
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
            Trusted by Industry Leaders
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            See how businesses like yours are transforming with SpynERP.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {testimonials.map((t, i) => (
            <motion.div
              key={t.name}
              className="rounded-2xl bg-navy-800/80 border border-white/10 p-6 sm:p-8 shadow-xl"
              initial={{ opacity: 0, y: 32 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * i }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
            >
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span key={star} className="text-amber-400 text-lg">★</span>
                ))}
              </div>
              <p className="text-gray-300 leading-relaxed mb-6">&ldquo;{t.quote}&rdquo;</p>
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold">
                  {t.initial}
                </span>
                <div>
                  <div className="font-semibold text-white">{t.name}</div>
                  <div className="text-sm text-gray-500">{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ----- CTA -----
function CTA() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section ref={ref} className="py-20 sm:py-28 bg-gradient-to-br from-brand-600 to-brand-800 relative overflow-hidden">
      <motion.div
        className="absolute inset-0 opacity-30"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 1 }}
        style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.08) 0%, transparent 50%)',
        }}
      />
      <div className="relative max-w-3xl mx-auto text-center px-4">
        <motion.h2
          className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
        >
          Ready to Transform Your Business?
        </motion.h2>
        <motion.p
          className="text-brand-100 text-lg mb-10"
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Join thousands of businesses already using SpynERP to streamline operations and drive growth.
        </motion.p>
        <motion.div
          className="flex flex-wrap justify-center gap-4"
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Link to="/register">
          <motion.span
            className="inline-flex items-center gap-2 rounded-xl bg-white text-brand-700 px-6 py-3.5 font-semibold shadow-xl hover:bg-gray-100 transition-colors block"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            Start Your Free Trial →
          </motion.span>
        </Link>
          <motion.a
            href="#contact"
            className="inline-flex items-center gap-2 rounded-xl border-2 border-white/50 text-white px-6 py-3.5 font-semibold hover:bg-white/10 transition-colors"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            Contact Sales
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}

// ----- Footer -----
const footerProduct = ['Features', 'Pricing', 'Integrations', 'Updates'];
const footerResources = ['Documentation', 'Help Center', 'Community', 'Blog'];
const footerCompany = ['About Us', 'Careers', 'Contact', 'Partners'];

function Footer() {
  return (
    <footer className="bg-navy-900 text-gray-400 py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 lg:gap-12 mb-12">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-lg bg-brand-600 flex items-center justify-center text-white">
              <Logo className="w-5 h-5 shrink-0" aria-hidden />
            </span>
              <span className="font-bold text-white text-lg">SpynERP</span>
            </div>
            <p className="text-sm leading-relaxed mb-3">
              The modern ERP platform for businesses that demand excellence.
            </p>
            <span className="inline-flex items-center gap-2 text-sm">
              <span className="w-4 h-4 rounded-full bg-emerald-500/80 flex items-center justify-center text-white text-xs">✓</span>
              SOC 2 Compliant
            </span>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-4">Product</h4>
            <ul className="space-y-2">
              {footerProduct.map((link) => (
                <li key={link}>
                  <a href={`#${link.toLowerCase().replace(/\s+/g, '-')}`} className="hover:text-white transition-colors">{link}</a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-4">Resources</h4>
            <ul className="space-y-2">
              {footerResources.map((link) => (
                <li key={link}>
                  <a href={`#${link.toLowerCase().replace(/\s+/g, '-')}`} className="hover:text-white transition-colors">{link}</a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-4">Company</h4>
            <ul className="space-y-2">
              {footerCompany.map((link) => (
                <li key={link}>
                  <a href={`#${link.toLowerCase().replace(/\s+/g, '-')}`} className="hover:text-white transition-colors">{link}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm">© 2026 SpynERP. All rights reserved.</div>
          <div className="flex items-center gap-6 text-sm">
            <a href="#privacy" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="#terms" className="hover:text-white transition-colors">Terms of Service</a>
            <a href="#cookies" className="hover:text-white transition-colors">Cookie Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ----- App -----
function App() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Routes>
        <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
        <Route path="/admin/login" element={<AdminGuestRoute><AdminLoginPage /></AdminGuestRoute>} />
        <Route path="/admin" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="merchant" element={<AdminMerchant />} />
          <Route path="merchant/:id" element={<AdminMerchantView />} />
          <Route path="subscription-config" element={<AdminSubscriptionConfig />} />
          <Route path="accounting" element={<AdminAccounting />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>
        <Route path="/employee-login" element={<GuestRoute><EmployeeLoginPage /></GuestRoute>} />
        <Route path="/register" element={<GuestRoute><RegistrationPage /></GuestRoute>} />
        <Route path="/employee" element={<ProtectedRoute><EmployeeDashboard /></ProtectedRoute>} />
        <Route path="/dashboard/*" element={<ProtectedRoute><MerchantDashboard /></ProtectedRoute>} />
        <Route
          path="/"
          element={
            <>
              <Header />
              <main>
                <Hero />
                <Stats />
                <Features />
                <Industries />
                <Testimonials />
                <CTA />
                <Footer />
              </main>
            </>
          }
        />
      </Routes>
    </div>
  );
}

export default App;
